import { AddressDisplay, Badge, cn } from "@gotts.ai/ui";
import { FileCode2 } from "lucide-react";

import { PcbCard } from "../../components/PcbCard";
import { TokenIcon } from "../../components/TokenIcon";

import { GROUP_COLORS, GROUP_ORDER } from "./types";

import type { ContractGroup, ContractOption } from "./types";

interface ContractEmptyStateProps {
  options: ContractOption[];
  favorites: Set<string>;
  onSelect: (value: string) => void;
}

export function ContractEmptyState({
  options,
  favorites,
  onSelect,
}: ContractEmptyStateProps) {
  if (options.length === 0) {
    return (
      <PcbCard label="DEPLOY" cut={10}>
        <div className="py-12 text-center">
          <p className="text-sm text-[var(--color-text-muted)]">
            Waiting for deployment...
          </p>
          <p className="text-[var(--color-text-muted)]/60 mt-1 text-[10px]">
            Start the devenv to deploy contracts
          </p>
        </div>
      </PcbCard>
    );
  }

  // Favorites first
  const pinnedOptions = options.filter((o) => favorites.has(o.value));
  const grouped = new Map<ContractGroup, ContractOption[]>();
  for (const group of GROUP_ORDER) {
    if (group === "Pinned" || group === "Custom") continue;
    const items = options.filter((o) => o.group === group);
    if (items.length > 0) grouped.set(group, items);
  }

  return (
    <div className="space-y-5">
      {/* Favorites */}
      {pinnedOptions.length > 0 && (
        <div>
          <h3 className="section-tag">Pinned</h3>
          <div className="grid grid-cols-2 gap-[2px] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {pinnedOptions.map((opt) => (
              <ContractCard
                key={opt.value}
                opt={opt}
                onSelect={onSelect}
                pinned
              />
            ))}
          </div>
        </div>
      )}

      {/* Grouped */}
      {[...grouped.entries()].map(([group, items]) => (
        <div key={group}>
          <h3 className="section-tag">{group}</h3>
          <div className="grid grid-cols-2 gap-[2px] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {items.map((opt) => (
              <ContractCard key={opt.value} opt={opt} onSelect={onSelect} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContractCard({
  opt,
  onSelect,
  pinned,
}: {
  opt: ContractOption;
  onSelect: (value: string) => void;
  pinned?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onSelect(opt.value);
      }}
      className={cn(
        "group relative text-left transition-all duration-300",
        /* NieR glass surface */
        "bg-[rgba(20,20,18,0.50)] p-3.5",
        "border-l-2 border-l-transparent",
        /* Hard-edge shadow + gold border simulation */
        "shadow-[inset_0_1px_0_rgba(201,168,76,0.06),inset_0_-1px_0_rgba(0,0,0,0.05)]",
        /* Hover: lift + hard-edge shadow + gold left accent */
        "hover:-translate-y-1 hover:bg-[rgba(24,23,18,0.65)]",
        "hover:border-l-[rgba(201,168,76,0.45)]",
        "hover:shadow-[5px_5px_0_rgba(0,0,0,0.35),inset_0_1px_0_rgba(201,168,76,0.12),inset_0_-1px_0_rgba(0,0,0,0.05)]",
      )}
    >
      {/* Top gold shimmer on hover */}
      <div className="duration-400 pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[var(--color-gold)] to-transparent opacity-0 transition-opacity group-hover:opacity-[0.5]" />

      <div className="relative flex items-start gap-2.5">
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center transition-colors duration-300",
            "bg-[rgba(255,255,255,0.04)] group-hover:bg-[rgba(201,168,76,0.08)]",
          )}
        >
          {opt.tokenSymbol ? (
            <TokenIcon symbol={opt.tokenSymbol} size={18} />
          ) : (
            <FileCode2
              size={13}
              className="text-[var(--color-text-muted)] transition-colors group-hover:text-[var(--color-gold)]"
            />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <span className="truncate font-mono text-[11px] font-medium text-[var(--color-text)] transition-colors group-hover:text-[var(--color-gold)]">
            {opt.label}
          </span>
          <div className="mt-0.5">
            <AddressDisplay
              address={opt.address}
              leadChars={6}
              tailChars={4}
              fontSize={9}
            />
          </div>
          <div className="mt-1.5">
            <Badge
              className={cn(
                "text-[7px] transition-opacity",
                GROUP_COLORS[opt.group] ?? "bg-gray-500/20 text-gray-400",
              )}
            >
              {opt.group}
            </Badge>
          </div>
        </div>
      </div>

      {/* Pinned indicator */}
      {pinned && (
        <span className="absolute right-2 top-2 text-[8px] text-yellow-500/60">
          {"\u2605"}
        </span>
      )}
    </button>
  );
}
